import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DecimalFieldPolicy } from '../../utils/decimalInput';
import { DecimalEmptyPolicy, DecimalPublishEvent, useDecimalDraft } from '../../hooks/useDecimalDraft';

export type DecimalTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'onFocus' | 'onBlur' | 'onKeyDown' | 'type' | 'error'> & {
    value: unknown;
    policy: DecimalFieldPolicy;
    emptyPolicy: DecimalEmptyPolicy;
    onValueChange: (value: number | null) => void;
    onValidityChange?: (isValid: boolean) => void;
    /** Text that is not a usable number yet, while typing and when committed. */
    onInvalidText?: (raw: string) => void;
    resetKey?: string | number;
    formatBlurred?: (value: unknown) => string;
    /** Field name interpolated in the error message. */
    fieldLabel?: string;
    /** Error decided by the owner after the value is usable (domain rules). */
    externalError?: string | null;
};

const DecimalTextField: React.FC<DecimalTextFieldProps> = ({
    value,
    policy,
    emptyPolicy,
    onValueChange,
    onValidityChange,
    onInvalidText,
    resetKey,
    formatBlurred,
    fieldLabel = '',
    externalError,
    helperText,
    inputProps,
    ...textFieldProps
}) => {
    const { t } = useTranslation();

    const handlePublish = (event: DecimalPublishEvent) => {
        if (event.kind === 'value') onValueChange(event.value);
        else if (event.kind === 'empty') onValueChange(event.value === '' ? null : event.value);
        else onInvalidText?.(event.raw);
    };

    const draft = useDecimalDraft({ value, policy, emptyPolicy, formatBlurred, onPublish: handlePublish, onValidityChange, resetKey });
    const { value: inputValue, onChange, onFocus, onBlur, onKeyDown, ...inputAttributes } = draft.inputProps;

    const decimalError = draft.errorKind ? t(draft.errorKind === 'ambiguous' ? 'validation_ambiguous_decimal_separator' : 'validation_must_be_number', { field: fieldLabel }) : null;
    const errorText = decimalError ?? externalError ?? null;

    return (
        <TextField
            {...textFieldProps}
            value={inputValue}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            error={errorText !== null}
            helperText={errorText ?? helperText}
            inputProps={{ ...inputProps, ...inputAttributes, 'aria-invalid': errorText !== null }}
        />
    );
};

export default DecimalTextField;
